import type { Locale } from '@worldcoo/monolith/core';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isServer } from '@tanstack/react-query';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/** Node lacks full Intl for these, so SSR and client would format differently. */
export function normalizeLocale(locale: string): string {
	return locale === 'eu' || locale === 'gl' || locale === 'it' ? 'es' : locale;
}

const numberFormatCache = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(locale: string, decimalDigits: number, alwaysGroup: boolean): Intl.NumberFormat {
	const normalizedLocale = normalizeLocale(locale);

	const cacheKey = `${normalizedLocale}-${decimalDigits}${alwaysGroup ? '-grouped' : ''}`;

	let formatter = numberFormatCache.get(cacheKey);
	if (!formatter) {
		formatter = new Intl.NumberFormat(normalizedLocale, {
			style: 'decimal',
			minimumFractionDigits: decimalDigits,
			maximumFractionDigits: decimalDigits,
			// `undefined` keeps the locale's own rule; 'always' overrides its `minimumGroupingDigits`.
			useGrouping: alwaysGroup ? 'always' : undefined,
		});
		numberFormatCache.set(cacheKey, formatter);
	}

	return formatter;
}

/**
 * `alwaysGroup`: Spanish, Catalan and Galician leave `1303` ungrouped (`minimumGroupingDigits: 2`), right
 * for prose and wrong for a headline counter that must read as thousands.
 */
export function formatNumber(locale: string, number: number, decimalDigits = 2, options?: { alwaysGroup?: boolean }) {
	return getNumberFormatter(locale, decimalDigits, options?.alwaysGroup ?? false).format(number);
}

/** `+101M`, `+432K`, `+7M €`; below 1000 a plain locale-formatted number. */
export function formatCompactNumber(
	locale: string,
	number: number,
	options?: { suffix?: string; decimalDigits?: number },
) {
	const suffix = options?.suffix ? ` ${options.suffix}` : '';
	const digits = options?.decimalDigits ?? 0;

	if (number >= 1_000_000) {
		const value = digits === 0 ? Math.floor(number / 1_000_000) : (number / 1_000_000).toFixed(digits);
		return `+${value}M${suffix}`;
	}
	if (number >= 1_000) {
		const value = digits === 0 ? Math.floor(number / 1_000) : (number / 1_000).toFixed(digits);
		return `+${value}K${suffix}`;
	}

	return `${formatNumber(locale, number, 0)}${suffix}`;
}

const currencyFormatCache = new Map<string, Intl.NumberFormat>();

/** `amount` in the major unit (euros, not cents). */
export function formatCurrency(locale: string, amount: number, currency = 'EUR', maximumFractionDigits = 0): string {
	const normalizedLocale = normalizeLocale(locale);
	const cacheKey = `${normalizedLocale}-${currency}-${maximumFractionDigits}`;

	let formatter = currencyFormatCache.get(cacheKey);
	if (!formatter) {
		formatter = new Intl.NumberFormat(normalizedLocale, {
			style: 'currency',
			currency,
			maximumFractionDigits,
			minimumFractionDigits: 0,
		});
		currencyFormatCache.set(cacheKey, formatter);
	}

	return formatter.format(amount);
}

export function formatCurrencyFromCents(
	locale: string,
	cents: number,
	currency = 'EUR',
	maximumFractionDigits = 0,
): string {
	return formatCurrency(locale, Math.round(cents / 100), currency, maximumFractionDigits);
}

const listFormatCache = new Map<string, Intl.ListFormat>();

function getListFormatter(locale: Locale): Intl.ListFormat {
	const normalizedLocale = normalizeLocale(locale);

	let formatter = listFormatCache.get(normalizedLocale);
	if (!formatter) {
		formatter = new Intl.ListFormat(normalizedLocale, {
			style: 'long',
			type: 'conjunction',
		});
		listFormatCache.set(normalizedLocale, formatter);
	}

	return formatter;
}

export const formatList = (locale: Locale, items: string[]) => {
	return getListFormatter(locale).format(items);
};

export const savedTaxAmount = (amount: number) => {
	// Spanish residents, Real Decreto-ley 6/2023: https://www.boe.es/buscar/act.php?id=BOE-A-2023-25758&p=20250103&tn=1#a1-41
	return Math.min(amount, 250) * 0.8 + (amount - Math.min(amount, 250)) * 0.4;
};

/** Local date, for inputs. */
export const formatDate = (dateString: string | undefined) => {
	if (!dateString) return;

	const date = new Date(dateString);

	const year = date.getFullYear().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');

	return `${year}-${month}-${day}`;
};

/** Local datetime, for inputs. */
export const formatDatetime = (dateString: string | undefined) => {
	if (!dateString) return;

	const date = new Date(dateString);

	const year = date.getFullYear().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');
	const hours = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function preloadImageOnClient(src: string | undefined) {
	if (isServer) return;
	if (!src) return;
	const img = new Image();
	img.fetchPriority = 'low';
	img.src = src;
}

export function calculateDaysLeft(date: string) {
	return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)).toString();
}

export function calculateExpiryDate(days: number) {
	return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
}

export const heroImageSize = { width: 630, height: 433, className: 'lg:h-[433px] aspect-630/433' };
export const campaignImageSize = { width: 381, height: 250, className: 'h-[250px] aspect-381/250' };
